import { Autocomplete } from '@mui/material';
import { Fragment, memo, ReactElement, useCallback, useMemo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Territory } from 'shell/models/territory';
import debounce from 'shell/utils/debounce';
import { BaseTableRows } from 'shell/components/shared/table/types';
import { Operation } from 'shell/view-models/operation';

export interface TerritoryVM extends BaseTableRows {
  code: string;
  description: string;
  remove?: ReactElement;
}

interface FormTerritoryProps {
  selectedValue: TerritoryVM | null;
  handleChange: (value: TerritoryVM | null) => void;
  disabled?: boolean;
  error?: boolean;
}

const TerritoryAutoCompleteComponent = ({
  selectedValue,
  handleChange,
  disabled,
  error,
}: FormTerritoryProps): React.ReactElement => {
  const {
    get: getTerritories,
    data,
    error: errorTerritory,
    loading,
    abort,
  } = useHttpFetch<HttpResponseList<Territory>>(`${__MUNICIPALITY_API_URL__}/geo/territorio`, {
    data: {},
  });

  const territories: Array<TerritoryVM> = useMemo(() => {
    if (!!errorTerritory || !data?.payload) {
      return [];
    }
    return data.payload.list.map((territory) => {
      return {
        description: territory.esonimo,
        code: territory.key.codice,
      };
    });
  }, [data, errorTerritory]);

  const defaultProps = useMemo(
    () => ({
      options: territories,
      getOptionLabel: (option: TerritoryVM) => option.description || '',
    }),
    [territories]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        abort();
        getTerritories(`?filters=nome:${value}:${Operation.LIKE}`);
      },
      [getTerritories]
    )
  );

  return (
    <Fragment>
      <Autocomplete
        {...defaultProps}
        id="name"
        noOptionsText="Nessun elemento"
        disabled={disabled}
        value={selectedValue}
        loadingText="Caricamento..."
        onChange={(event, element) => {
          event.stopPropagation();
          handleChange(element);
        }}
        onInputChange={(event, value, reason) => {
          if (value.length >= 1 && reason === 'input') {
            handleSearch(value);
          }
        }}
        clearOnBlur={false}
        loading={loading}
        style={{ flex: 1 }}
        renderInput={(params) => (
          <PmiTextField {...params} label="Territorio *" variant="outlined" error={error} />
        )}
      />
    </Fragment>
  );
};

TerritoryAutoCompleteComponent.displayName = 'Territory Auto Complete component';

const TerritoryAutoComplete = memo(
  TerritoryAutoCompleteComponent
) as typeof TerritoryAutoCompleteComponent;

export default TerritoryAutoComplete;
